import React, { useState } from 'react';
import {
  Table,
  Col,
  Input,
  Card,
  CardBody,
  Spinner,
  CardFooter,
  Button,
} from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { mdiAccountOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useNavigate } from 'react-router-dom';
import leaveEnquiryHeaders, {
  LEAVE_STATUS_BADGE_STYLES,
  LEAVE_TYPES_BADGE_STYLES,
} from './constants';
import { useTableHook } from '../../../components/hooks';
import {
  fetchEmployeeLeaves,
  fetchEmployeeLeaveTypes,
  fetchEmployeeLeavesColumn,
} from '../../../store/apis/leaveService';
import {
  FilterDropdown,
  TableFilterBubbles,
  TableSortDisplayArrow,
  Badge,
  UserAsyncAutocompleteSelect,
  DisplayModal,
  TablePagination,
  DatePicker,
  GeneralAttachmentTableModal,
} from '../../../components/Common';
import { AuthUtil, FileUtil, StringUtil } from '../../../utils';
import enquiryViewIcon from '../../../assets/icons/enquiryviewicon.png';
import enquiryDlIcon from '../../../assets/icons/enquirydlicon.png';
import '../index.scss';
import { fetchEmployeeData } from '../../../store/apis/employeeService';
import { downloadBlobType } from '../../../store/apis/commonService';
import { Messages } from '../../../common/messages';

function LeaveEnquiry() {
  document.title = 'Leave Enquiry | Envision Daikin';
  const navigate = useNavigate();
  const [isDisplayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);
  const [viewAttachmentsModalOpen, setViewAttachmentsModalOpen] =
    useState(false);
  const [viewAttachmentModalConfig, setViewAttachmentModalConfig] = useState({
    attachmentData: [],
    type: '',
  });

  // Table Hook
  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    applyExternalFilters,
    isLoadingData,
    isLoadingColumn,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'RequestDate',
      sortDirection: 'DESC',
      filters: [],
    },
    fetchFn: fetchEmployeeLeaves,
    fetchColumnFn: fetchEmployeeLeavesColumn,
    queryKeyPrefix: 'leaveEnquiry',
    columnKeyPrefix: 'leaveEnquiryColumn',
    tableName: 'LeaveEnquiry',
    defaultExternalFilters: {
      employeeId: {
        value: AuthUtil.getAuthUser()?.employeeId,
        label: AuthUtil.getAuthUser()?.userFullName,
      },
    },
  });

  const { data: leaveTypes } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: fetchEmployeeLeaveTypes,
    keepPreviousData: true,
  });

  const downloadFileMutatation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const handleAttachment = async (leaveData, type = 'view') => {
    if (leaveData?.AttachmentPath && leaveData?.AttachmentPath !== '-') {
      let attachmentsArray = [];
      try {
        attachmentsArray = StringUtil.checkStringOrJsonArray(
          leaveData?.AttachmentPath,
        );
        if (attachmentsArray.length === 1) {
          const fileBlob = await downloadFileMutatation.mutateAsync({
            fileName: attachmentsArray[0],
            type: 'Leave Documents',
            hrCoCd: leaveData?.CompanyCode.trim(),
          });
          const file = new File(
            [fileBlob],
            attachmentsArray[0] || 'attachment',
            { type: fileBlob.type },
          );
          if (type === 'view') {
            setDisplayModalFile(file);
            setDisplayModalOpen(true);
          } else {
            FileUtil.downloadBlobFile(
              file,
              attachmentsArray[0] || 'attachment',
            );
          }
        } else {
          setViewAttachmentsModalOpen(true);
          setViewAttachmentModalConfig({
            attachmentData: attachmentsArray,
            type: 'Leave Documents',
            hrCoCd: leaveData?.CompanyCode.trim(),
          });
        }
      } catch (error) {
        if (type === 'view') {
          toast.error('Failed to fetch attachment for preview.', {
            className: 'toast-error',
          });
        } else {
          toast.error('Failed to fetch attachment for download.', {
            className: 'toast-error',
          });
        }
      }
    } else if (type === 'view') {
      toast.error('No attachment found to preview.', {
        className: 'toast-error',
      });
    } else {
      toast.error('No attachment found to download.', {
        className: 'toast-error',
      });
    }
  };

  const handleLinkToView = item => {
    if (item?.RequestType?.trim() === 'WORK FROM HOME') {
      navigate(`/wfh/apply?ID=${item?.RequestID}`);
    } else {
      navigate(`/leave/apply?ID=${item?.RequestID}`);
    }
  };

  const renderTableBody = () => {
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={8}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_LEAVE
            )}
          </td>
        </tr>
      );
    }

    return tableData.map(item => (
      <tr
        key={item.RequestID}
        onClick={() => handleLinkToView(item)}
        className="cursor-pointer"
      >
        <td className="cursor-pointer text-decoration-underline text-primary text-header">
          {item.RequestDate}
        </td>
        <td>
          <Badge
            config={LEAVE_TYPES_BADGE_STYLES[item.RequestType]}
            labelType="TitleCase"
          >
            {item.RequestType}
          </Badge>
        </td>
        <td>{item['From Date']}</td>
        <td>{item['To Date']}</td>
        <td>{item.Days}</td>
        <td className="approver-col">
          <span>{item.Approver}</span>
        </td>
        <td>
          <Badge
            config={LEAVE_STATUS_BADGE_STYLES[item.Status]}
            labelType="TitleCase"
            badgeType="pill"
            dot
          >
            {item.Status}
          </Badge>
        </td>
        <td className="text-center attachments-col">
          <div className="d-flex flex-row align-items-center justify-content-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0"
              title="Download Leave"
              aria-label="Download Leave"
              onClick={e => {
                e.stopPropagation();
                handleAttachment(item, 'download');
              }}
            >
              <img src={enquiryDlIcon} alt="" style={{ height: '15px' }} />
            </button>
            <button
              type="button"
              className="btn btn-link p-0"
              title="View Leave"
              aria-label="View Leave"
              onClick={e => {
                e.stopPropagation();
                handleAttachment(item, 'view');
              }}
            >
              <img src={enquiryViewIcon} alt="" style={{ height: '12px' }} />
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  const externalFiltersValidation = () => {
    if (
      externalFilters.leaveFromDate &&
      externalFilters.leaveToDate &&
      new Date(externalFilters?.leaveFromDate) >
        new Date(externalFilters?.leaveToDate)
    ) {
      toast.error('To Date should be greater than equal to From Date.', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  return (
    <div className="page-content main-table-page">
      <Card className="enquiry-card">
        <CardBody className="p-0">
          <div className="p-3">
            <span className="fw-semibold fs-5">Leave Enquiry</span>
          </div>
          <div className="external-filter gap-2 mx-3 p-3">
            <Col className="external-filter-col">
              <b>Employee</b>
              <UserAsyncAutocompleteSelect
                autoCompleteFn={() => fetchEmployeeData()}
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.Code,
                    label: item.Description,
                    name: item.Description,
                    code: item.Code,
                    designation: item.Designation,
                    image: item?.Imagefile,
                  }))
                }
                onChange={e => {
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    employeeId: e,
                  }));
                }}
                placeholder="Search by Employee Name & ID"
                isClearable={false}
                isSearchable={false}
                value={externalFilters?.employeeId}
                inputIcon={
                  <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                }
                defaultShowMenu
                rightKeyAssessor="code"
                searchEmployee={
                  AuthUtil.getAuthUser()?.employeeId?.toLowerCase() !==
                  AuthUtil.getAuthUser()?.userName?.toLowerCase()
                }
                cursorType="default"
              />
            </Col>
            <Col className="external-filter-col">
              <b>Leave Type</b>
              <Input
                type="select"
                className="form-control external-filter-input"
                value={externalFilters.leaveType}
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    leaveType: e.target.value,
                  }))
                }
              >
                <option value="">Type</option>
                {leaveTypes?.map(item => (
                  <option key={item.Code} value={item.Code}>
                    {item.Description}
                  </option>
                ))}
              </Input>
            </Col>
            <Col className="external-filter-col">
              <b>From Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    leaveFromDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.leaveFromDate || ''}
              />
            </Col>
            <Col className="external-filter-col">
              <b>To Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    leaveToDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.leaveToDate || ''}
              />
            </Col>
            <Col className="d-flex align-items-center justify-content-end">
              <div className="external-filter-btns gap-2">
                <Button
                  type="button"
                  className="custom-btn"
                  onClick={() => {
                    if (!externalFiltersValidation()) {
                      return;
                    }
                    applyExternalFilters({
                      ...externalFilters,
                      employeeId: externalFilters?.employeeId?.value,
                    });
                  }}
                >
                  Search
                </Button>
              </div>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={leaveEnquiryHeaders}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive table-container">
            <Table
              hover
              className="custom-table align-middle text-nowrap"
              id="leave-enquiry"
            >
              <thead>
                <tr>
                  {Object.entries(leaveEnquiryHeaders).map(([key, label]) => (
                    <th key={key}>
                      <div className="d-flex flex-row align-items-center justify-content-start gap-1">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => {
                            handleAutoSort(key);
                          }}
                        >
                          {label}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="text-center">Attachments</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
      <DisplayModal
        isModalOpen={isDisplayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
      <GeneralAttachmentTableModal
        isModalOpen={viewAttachmentsModalOpen}
        toggle={() => setViewAttachmentsModalOpen(false)}
        attachmentData={viewAttachmentModalConfig.attachmentData}
        type={viewAttachmentModalConfig?.type}
        hrCoCd={viewAttachmentModalConfig?.hrCoCd}
      />
    </div>
  );
}

export default LeaveEnquiry;
